/**
 * Loans area (/loans/...) — LOP-owned (admin-tools-live-in-admin-portal, Bill 2026-10-29). Same pattern as the Retail area: a typed
 * page registry (./loansPages.ts) drives the menu, the home and the section pages, which list PAGES grouped by heading — never API
 * routes. A page is listed when its capability action is in pcb-loans-api's own answer (GET v3/loans/admin/capabilities); the
 * sections shown are that answer's sections. Pages live beside this file — LWP's write-up pages in ./writeups/ — and each needs a
 * line in LOANS_PAGES plus a <Route> under "loans" in App.tsx. Help text and the home's common tasks: ./loansGuide.ts.
 *
 * Redesign 0.53.0 (Bill 2026-11-03): the sections are in the shell's sidebar; the home is the shared platform home:
 *   Needs attention  only from real sources, each only when its page is in the capability answer:
 *                    Approvals - GET v3/asks?scope=admin&kind=loan-approval&status=Open (the Approvals monitor's own read);
 *                    Scheduled jobs - GET v3/loans/admin/detectors (each job's last run).
 *   Recent changes   GET v3/loans/admin/audit (pcb-loans-api 0.199.0+; action monitoring.audit): the latest admin writes with
 *                    who, when and the reason given. Shown only when the capability answer includes monitoring.audit.
 *
 * Same consent rule as the Hub: the loans API is called only with the shared backend token.
 */

import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { EmptyState, PageHeader, PlatformHome, type AttentionItem, type RecentChange } from '@retail-ops/panel-shell';
import { usePlatforms } from '@/contexts/PlatformsContext';
import { PlatformLayout } from '@/platforms/PlatformLayout';
import { SIX_SECTIONS } from '@/platforms/types';
import { Banner } from '@/components/primitives';
import { ConsentWaiting } from '@/platforms/hub/HubArea';
import { loansNav, loansTasksFor, useAdminShell } from '@/platforms/adminNav';
import { ageText, oldest, SectionCards, ShellLink, useSearchPick, whenText } from '@/platforms/homeShared';
import { SectionPageCards } from '@/platforms/retail/RetailArea';
import { LOANS_BASE, LOANS_PAGES, loansPagesFor, type LoansPage } from './loansPages';
import { LOANS_GUIDE } from './loansGuide';
import { loansApi } from './loansApi';
import { errorText } from './writeups/common';

export { LOANS_BASE };

/** Action id -> path under /loans, derived from LOANS_PAGES (kept for callers; the menus use the registry directly). */
export const LOANS_ACTION_PAGES: Record<string, string> = Object.fromEntries(
  LOANS_PAGES.map((pg) => [pg.action, pg.path.slice(LOANS_BASE.length + 1)]),
);

export function loansSectionOf(pathname: string): string | null {
  const seg = pathname.slice(LOANS_BASE.length + 1).split('/')[0] ?? '';
  return SIX_SECTIONS.some((s) => s.id === seg) ? seg : null;
}

export function LoansArea(): JSX.Element {
  const { status } = usePlatforms();
  const st = status.loans;
  if (st.kind === 'loading') return <div className="loading-inline">Checking your loans admin rights…</div>;
  if (st.kind === 'consent-waiting') return <ConsentWaiting what="Loans" />;
  if (st.kind === 'error') return <Banner tone="err">Could not reach the loans API: {st.message}</Banner>;
  if (st.kind !== 'ready') {
    return (
      <EmptyState title="You are not a loans administrator.">
        Loans admin is granted to one exact signed-in account — sign in with your Azure Admin account.
      </EmptyState>
    );
  }
  return (
    <PlatformLayout
      title={st.caps.displayName ?? 'Loans'}
      basePath={LOANS_BASE}
      enabled={new Set(st.caps.sections.map((s) => String(s.id)))}
      sectionOf={loansSectionOf}
    />
  );
}

/** The pages this admin can see, from the API's capability answer. */
function useLoansPages(): LoansPage[] {
  const { status } = usePlatforms();
  const st = status.loans;
  return useMemo(() => (st.kind === 'ready' ? loansPagesFor(st.caps.actions.map((a) => a.id)) : []), [st]);
}

interface ApprovalAsk {
  requestedAt: string | null;
  cardsFailed: number | null;
}
interface DetectorLite {
  label: string;
  lastRun: { status: string | null; error: string | null } | null;
  pending: { queuedWrites?: number; undecidedLines?: number } | null;
}

/** Needs attention: approvals waiting, and scheduled jobs whose last run failed. Only real reads; a failed read says so. */
function useLoansAttention(pages: ReadonlyArray<LoansPage>): { loading: boolean; items: AttentionItem[] } | null {
  const hasApprovals = pages.some((p) => p.action === 'monitoring.approvals');
  const hasJobs = pages.some((p) => p.action === 'monitoring.jobs');
  const [approvals, setApprovals] = useState<AttentionItem | null>(null);
  const [jobs, setJobs] = useState<AttentionItem | null>(null);

  useEffect(() => {
    if (!hasApprovals) return undefined;
    let cancelled = false;
    const path = `${LOANS_BASE}/monitoring/approvals`;
    void loansApi
      .get<{ count?: number; items?: ApprovalAsk[] }>('v3/asks', { scope: 'admin', kind: 'loan-approval', status: 'Open', top: 500 })
      .then((r) => {
        if (cancelled) return;
        const items = r.items ?? [];
        const n = items.length;
        const failed = items.filter((a) => (a.cardsFailed ?? 0) > 0).length;
        const age = ageText(oldest(items.map((a) => a.requestedAt)));
        setApprovals(
          n === 0
            ? { id: 'approvals', tone: 'ok', chip: 'All clear', title: 'Loan approvals', text: 'No approval requests are waiting.', path }
            : {
                id: 'approvals',
                tone: failed > 0 ? 'problem' : 'open',
                chip: `${n} open`,
                title: 'Approvals waiting for an answer',
                text: [age ? `The oldest was asked ${age} ago.` : null, failed > 0 ? `${failed} with a Teams card that failed to deliver.` : null]
                  .filter(Boolean)
                  .join(' ') || 'Open the approvals monitor to see who must answer.',
                path,
              },
        );
      })
      .catch((e: unknown) => {
        if (!cancelled) setApprovals({ id: 'approvals', tone: 'neutral', chip: 'Could not check', title: 'Loan approvals', text: errorText(e), path });
      });
    return () => {
      cancelled = true;
    };
  }, [hasApprovals]);

  useEffect(() => {
    if (!hasJobs) return undefined;
    let cancelled = false;
    const path = `${LOANS_BASE}/monitoring/jobs`;
    void loansApi
      .get<{ detectors?: DetectorLite[] }>('v3/loans/admin/detectors')
      .then((r) => {
        if (cancelled) return;
        const ds = r.detectors ?? [];
        const failed = ds.filter((d) => d.lastRun && (d.lastRun.error || /fail|error/i.test(d.lastRun.status ?? '')));
        const waiting = ds.reduce((sum, d) => sum + (d.pending?.undecidedLines ?? 0), 0);
        setJobs(
          failed.length > 0
            ? {
                id: 'jobs',
                tone: 'problem',
                chip: `${failed.length} failed`,
                title: 'Scheduled jobs',
                text: `Last run failed: ${failed.map((d) => d.label).join(', ')}.`,
                path,
              }
            : waiting > 0
              ? { id: 'jobs', tone: 'watch', chip: `${waiting} to decide`, title: 'Scheduled jobs', text: `${waiting} line${waiting === 1 ? ' is' : 's are'} waiting on a decision. No job’s last run failed.`, path }
              : { id: 'jobs', tone: 'ok', chip: 'All clear', title: 'Scheduled jobs', text: `${ds.length} job${ds.length === 1 ? '' : 's'}; no last run failed and nothing is waiting on a decision.`, path },
        );
      })
      .catch((e: unknown) => {
        if (!cancelled) setJobs({ id: 'jobs', tone: 'neutral', chip: 'Could not check', title: 'Scheduled jobs', text: errorText(e), path });
      });
    return () => {
      cancelled = true;
    };
  }, [hasJobs]);

  if (!hasApprovals && !hasJobs) return null;
  const items = [approvals, jobs].filter((x): x is AttentionItem => !!x);
  const expected = (hasApprovals ? 1 : 0) + (hasJobs ? 1 : 0);
  return { loading: items.length < expected, items };
}

interface LoansAuditEntry {
  atUtc: string;
  actor: string | null;
  action: string | null;
  label: string | null;
  target: string | null;
  reason: string | null;
  outcome: string | null;
}

/** Recent changes: the latest admin writes from loans.AdminAudit (GET v3/loans/admin/audit). A failed read says so. */
function useLoansRecent(enabled: boolean): { loading: boolean; error: string | null; items: RecentChange[] } | null {
  const [recent, setRecent] = useState<{ loading: boolean; error: string | null; items: RecentChange[] }>({ loading: true, error: null, items: [] });
  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    void loansApi
      .get<{ entries?: LoansAuditEntry[] }>('v3/loans/admin/audit', { top: 5 })
      .then((r) => {
        if (cancelled) return;
        setRecent({
          loading: false,
          error: null,
          items: (r.entries ?? []).slice(0, 5).map((e, i) => ({
            id: `${e.atUtc}-${i}`,
            what: [e.label ?? e.action ?? 'Change', e.target, e.outcome && e.outcome !== 'ok' && e.outcome !== 'success' ? `(${e.outcome})` : null]
              .filter(Boolean)
              .join(' — '),
            why: e.reason,
            who: e.actor ?? 'Unknown',
            when: whenText(e.atUtc),
          })),
        });
      })
      .catch((e: unknown) => {
        if (!cancelled) setRecent({ loading: false, error: errorText(e), items: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [enabled]);
  return enabled ? recent : null;
}

export function LoansHome(): JSX.Element {
  const { status } = usePlatforms();
  const st = status.loans;
  const pages = useLoansPages();
  const { searchItems } = useAdminShell(LOANS_BASE);
  const onPick = useSearchPick();
  const attention = useLoansAttention(pages);
  const canAudit = st.kind === 'ready' && st.caps.actions.some((a) => a.id === 'monitoring.audit');
  const recent = useLoansRecent(canAudit);
  if (st.kind !== 'ready') return <></>;
  const nav = loansNav(st.caps);
  return (
    <PlatformHome
      title="Loans administration"
      intro="Pick a task, or search for what you want to do. Every change asks for a reason and is recorded with your name, the time, and the before and after values."
      Link={ShellLink}
      searchItems={searchItems}
      onSearchPick={onPick}
      searchPlaceholder={'What do you want to do?  e.g. "give pipeline access", "post-closing milestone"'}
      attention={attention}
      tasks={loansTasksFor(st.caps)}
      recent={recent}
      footer={
        pages.length === 0 ? (
          <EmptyState title="There are no Loans pages for your account yet." />
        ) : (
          <SectionCards nav={nav} />
        )
      }
    />
  );
}

export function LoansSectionPage(): JSX.Element {
  const { section } = useParams();
  const { status } = usePlatforms();
  const st = status.loans;
  const def = SIX_SECTIONS.find((s) => s.id === section);
  const pages = useLoansPages().filter((pg) => pg.section === section);
  if (!def || st.kind !== 'ready') return <EmptyState title="There is no such section.">Choose a section from the menu.</EmptyState>;

  return (
    <div>
      <PageHeader title={def.label} intro={def.holds} />
      {pages.length === 0 && (
        <EmptyState title="Nothing in this section for your account yet.">
          {def.id === 'find' ? 'Loans has no record lookup for your account yet.' : 'If you expect pages here, ask for the loans admin grant.'}
        </EmptyState>
      )}
      <SectionPageCards pages={pages.map((pg) => ({ ...pg, help: LOANS_GUIDE[pg.path]?.help }))} />
    </div>
  );
}
